import { useEffect, useState } from 'react';
import { Loader, Text } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconLogin, IconAlertCircle } from '@tabler/icons-react';
import {
  clearSsoReturn,
  peekSsoReturn,
  isSameOriginReturnUrl,
} from '@/services/Authentication';
import { clientRoutes, serverPath } from '@/ApiPaths';
import { getJson } from '@/services/httpClient';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import { AuthPageShell } from '@/components/auth/AuthPageShell';
import authClasses from '@/components/auth/AuthPage.module.css';

/**
 * SSO callback page: after successful login, redirect user to customer site (return_url) with state.
 * Same-origin return_url is always allowed; cross-origin requires server allowlist.
 */
export function LoginReturn() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const sso = peekSsoReturn();
      clearSsoReturn();

      if (!sso) {
        navigate(clientRoutes.dashboard, { replace: true });
        return;
      }

      const go = () => {
        const separator = sso.return_url.includes('?') ? '&' : '?';
        window.location.href = `${sso.return_url}${separator}state=${encodeURIComponent(sso.state)}`;
      };

      try {
        if (isSameOriginReturnUrl(sso.return_url)) {
          go();
          return;
        }

        const checkUrl = `${serverPath}auth/redirect-allowed?return_url=${encodeURIComponent(sso.return_url)}`;
        const data = await getJson<{ allowed: boolean }>(checkUrl);
        if (cancelled) return;

        if (!data.allowed) {
          setError(t('loginReturn.redirectNotAllowed', 'Return URL is not allowed'));
          return;
        }

        go();
      } catch {
        if (!cancelled) {
          setError(t('loginReturn.redirectFailed', 'Could not complete redirect'));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, t]);

  return (
    <AuthPageShell
      title={error ? t('loginReturn.errorTitle', 'Redirect failed') : t('loginReturn.title')}
      subtitle={error ? error : t('loginReturn.subtitle')}
      icon={
        error ? (
          <IconAlertCircle size={22} stroke={1.75} />
        ) : (
          <IconLogin size={22} stroke={1.75} />
        )
      }
      accentColor={DEFAULT_QR_CODE_COLOR}
    >
      <div className={authClasses.statusCenter}>
        {error ? (
          <Text size="sm" c="dimmed" ta="center">
            {t('loginReturn.errorHint', 'You can close this tab or go to the dashboard.')}
          </Text>
        ) : (
          <Loader size="sm" color={DEFAULT_QR_CODE_COLOR} />
        )}
      </div>
    </AuthPageShell>
  );
}
